import { useState } from "react";
import { useTranslations } from "next-intl";
import { IconPencil, IconCheck, IconX } from "@tabler/icons-react";
import { Field, isComputedField } from "@/types/Field";
import { Guest } from "@/types/Guest";
import { DataType } from "@/types/enums/DataType";
import {
  DETAIL_READ_ONLY_BOX,
  DETAIL_READ_ONLY_TEXT,
} from "../../Inputs/detailFieldChrome";
import {
  isArrivalNoticeLayoutField,
  isPortalLinkLayoutField,
  isReadOnlyLayoutField,
} from "./guestDetailLayout";
import { PortalLinkLayoutCell } from "./PortalLinkLayoutCell";
import { ArrivalNoticeLayoutCell } from "./ArrivalNoticeLayoutCell";
import { DataTypeInput } from "../../Inputs/DataTypeInput";
import { TableCellContent } from "../../TableCell/TableCellContent";
import { CoveringLoadingSpinner } from "../../LoadingSpinner/CoveringLoadingSpinner";
import { translateBackendError } from "@/lib/i18n/translateBackendError";
import { guestTypeBlockReason } from "@/lib/guests/guestTypeMutability";
import { CreationFieldsRef } from "../../PCreateGuest/PCreateGuest";
import {
  LockedFieldWrapper,
  FieldLockIcon,
} from "../../PCreateGuest/components/useConditionalFieldLock";
import {
  hasSuppliedFieldValue,
  normalizeFieldValue,
  resolveSuppliedFieldKey,
} from "@/lib/utils/input-validator";

/**
 * Everything a single layout cell needs, shared across all cells of one
 * popup render (built once in GuestDetailLayoutView).
 */
export type LayoutFieldCtx = {
  /** Create lane vs edit lane — decides whether a configured default may be
   *  shown. See `additionalFieldValue`. */
  isCreate: boolean;
  creationFields: CreationFieldsRef;
  setCreationFields: (fields: CreationFieldsRef) => void;
  canWriteGuests: boolean;
  viewOnly: boolean;
  getLock: (
    field: Field,
    label: string,
  ) => {
    conditionUnmet: boolean;
    isLocked: boolean;
    lockedAriaLabel: string;
    onLockedClick: () => void;
  };
  optionFiltered: (field: Field) => Field;
  onSaveCredential?: (fieldName: string, value: string) => Promise<void>;
  fieldErrors?: Record<string, string>;
  onClearFieldError?: (fieldName: string) => void;
  /** Capacity-full SELECT options (greyed) — additional fields only. */
  fullOptionsFor: (field: Field) => string[] | undefined;
  /** Guest the popup shows — enables the IMAGE additional-field upload flow
   *  (presign/confirm are guest-scoped). Undefined pre-create. */
  currentGuestId?: string;
  /**
   * The guest the popup shows — the ONLY value source for display-only cells,
   * read straight off the fetched Guest.
   *
   * Deliberately NOT routed through `creationFields`: the popup's dirty check
   * is a whole-object JSON.stringify diff (GuestActions) and GuestActions also
   * spreads `...creationFields` into a Guest, so a derived value there means a
   * permanently-lit Save button and a field riding along in the local cache.
   *
   * Optional on purpose — the builder canvas has no guest and must keep
   * compiling; its read-only cells render an em-dash.
   */
  guest?: Guest;
  /** IMAGE upload confirm persisted a value server-side: sync it into BOTH
   *  the live form state and the pristine baseline (creationFieldsDb) so the
   *  popup's Save button doesn't light up dirty from the echo. */
  onAdditionalFieldPersisted?: (fieldName: string, value: unknown) => void;
  /**
   * A cell's box holds text its form value does not (today: the EMAIL editor
   * with an unstorable address). The host's Save/Create gate reads the set
   * this feeds — see useInvalidFields.
   */
  onFieldInvalid?: (fieldName: string, invalid: boolean) => void;
  /**
   * The lane fixed the guest type, so its input is shown but inert. This is
   * the create dialog's `companion || mainGuest` rule (formerly
   * `disabled={companion || mainGuest}` in GeneralInfos): the companion-request
   * lane may only create companions and the registrant lane only main guests,
   * and both write the type themselves.
   *
   * Deliberately `disabled`, not `locked` — `locked` means "a condition
   * filtered this out, click to override" everywhere else, and this cannot be
   * overridden. Same treatment as the update-path block below it.
   */
  guestTypeLocked?: boolean;
  /**
   * The event the popup belongs to — with `guest`/`currentGuestId` what makes
   * the portal-link cell (W3-3) a live mint button. Absent in the builder
   * canvas, where the cell is a static preview.
   */
  eventId?: string;
  /** The event's plugins — the portal-link cell needs ATTACHMENTS. */
  plugins?: readonly string[];
  /**
   * The popup offers no portal link here although a cell is placed: an
   * archived guest (the archived list — the header shows "Unarchive" only) or
   * the pending-request decision surface (no header actions at all). The cell
   * then renders nothing, like the header action.
   */
  portalLinkHidden?: boolean;
  /**
   * A companion's main guest, by name — the "Ankunft melden an" cell names it
   * on an inherited link (plan arrival-automation Rev. 4 §1.8). Absent = not
   * a companion / not known: the cell says "via the main guest".
   */
  mainGuestName?: string;
};

/**
 * Effective additional-field value for the surface — decided by the LANE,
 * because that is where the backend decides it.
 *
 * `applyAdditionalFieldDefaults` is CREATE-ONLY. `GuestService.java:292` states
 * it outright: "Defaults are create-only … and never re-applied, so clearing a
 * field never resurrects its default." It runs in `processGuestsBatch` and
 * `ContactService`, nowhere else.
 *
 *   isCreate → no stored value exists and the backend WILL inject the default
 *              for an absent-or-blank field. Showing it is showing the truth.
 *   edit     → the stored value is authoritative; no lane injects a default
 *              over it. Showing one displays a value the database does not hold
 *              and nothing will ever write, and — because this is the `value` of
 *              a controlled input — it also snaps back the moment the organizer
 *              clears the field, making it uncleanable.
 *
 * The lane, not the widget, is the reason. This field is written by guest
 * import, an UPDATE_GUEST automation, bulk edit, a template clone and the
 * 2026-08-23 backfill, none of which open the drawer; the rule has to hold for
 * a blank that arrived any of those ways.
 *
 * A previous cut used a blank-aware check with NO lane distinction, justified as
 * "matching the backend's applyAdditionalFieldDefaults" — which was true of the
 * create lane and false of the edit lane the same component also serves.
 */
export function additionalFieldValue(
  creationFields: CreationFieldsRef,
  field: Field,
  isCreate: boolean,
): unknown {
  // Read through the RESOLVED key, not `field.name`: the lookup matches
  // case-insensitively (mirroring Java), so the stored spelling may differ from
  // the schema's. Reading the exact name after asking "is it supplied?" returns
  // `undefined` for a field that IS supplied, which flips this controlled input
  // to uncontrolled mid-render.
  const suppliedKey = resolveSuppliedFieldKey(
    creationFields.additionalFields,
    field.name,
  );
  if (suppliedKey !== undefined &&
      hasSuppliedFieldValue(creationFields.additionalFields, field.name)) {
    const stored = creationFields.additionalFields![suppliedKey];
    // NO `String(...)`. This function feeds `DataTypeInput` for EVERY DataType
    // and there is no type branch above it, so a coercion here is not a
    // formatting choice — it is data loss on the two types that are not
    // scalars. `["VIP","Gold"]` became `"VIP,Gold"`, which the widget re-split
    // into one bogus option that matched nothing and saved cleanly; an IMAGE
    // object became `"[object Object]"`, which fails `isAfImageValue` and
    // renders the guest's photo as an inert hint.
    //
    // The predecessor was `... as string` — a CAST, which is why this was
    // invisible: the declared return type had always been a lie, and switching
    // to a real coercion made it true by destroying the value. `tsc` cannot
    // see that change, and neither host component is mounted by any test.
    //
    // `TableCellContent` takes `value: unknown` and coerces per branch, so the
    // scalar widgets are unaffected by passing the value through.
    if (typeof stored === "string") return normalizeFieldValue(stored);
    // A MULTI_SELECT keeps its shape; only the ELEMENTS are normalized. Blank
    // elements are NOT dropped here — this is a read for display, and the drop
    // belongs on the write lanes (`sanitizeGuest`, `normalizeCreationFields`),
    // which already do it. Hiding a stored blank from the editor would make it
    // unremovable.
    if (Array.isArray(stored)) {
      return stored.map((el) => (typeof el === "string" ? normalizeFieldValue(el) : el));
    }
    return stored ?? "";
  }
  // Only the create lane may fall back to the configured default.
  if (isCreate && field.defaultValue != null) return field.defaultValue;
  return "";
}

/** Inline-editable credential cell (username / code) — pencil starts an edit,
 *  check saves through onSaveCredential (backend + duplicate validation), X
 *  cancels. Ported unchanged from GeneralInfos, one instance per cell. */
function CredentialCell({ field, ctx }: { field: Field; ctx: LayoutFieldCtx }) {
  const t2 = useTranslations("guest-fields");
  const tErrors = useTranslations("errors");
  const [editing, setEditing] = useState(false);
  const [editingValue, setEditingValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lock = ctx.getLock(field, t2(field.name));
  const storedValue =
    (ctx.creationFields?.[field.name as keyof CreationFieldsRef] as string) ||
    "";

  const confirmEdit = async () => {
    if (ctx.onSaveCredential) {
      setSaving(true);
      setError(null);
      try {
        await ctx.onSaveCredential(field.name, editingValue);
        ctx.setCreationFields({
          ...ctx.creationFields,
          [field.name]: editingValue,
        });
        setEditing(false);
      } catch (err) {
        const cause = err instanceof Error ? err : "unknown";
        setError(translateBackendError(tErrors, cause));
      } finally {
        setSaving(false);
      }
    } else {
      ctx.setCreationFields({
        ...ctx.creationFields,
        [field.name]: editingValue,
      });
      setEditing(false);
    }
  };

  return (
    <div className="flex flex-col space-y-1.5">
      <div className="flex items-center gap-1.5">
        <p className="body-3-medium">{t2(field.name)}</p>
        {field.required && (
          <span className="font-error body-3-medium" aria-hidden>
            *
          </span>
        )}
        {lock.isLocked && <FieldLockIcon />}
        {ctx.canWriteGuests &&
          !lock.isLocked &&
          (editing ? (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={confirmEdit}
                disabled={saving}
                className="font-success hover:opacity-80 transition-colors disabled:opacity-50"
              >
                <IconCheck size={14} stroke={1.5} />
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setError(null);
                }}
                disabled={saving}
                className="font-error hover:opacity-80 transition-colors disabled:opacity-50"
              >
                <IconX size={14} stroke={1.5} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                setEditing(true);
                setError(null);
                setEditingValue(storedValue);
              }}
              disabled={saving}
              className="font-tertiary hover:font-primary transition-colors disabled:opacity-50"
            >
              <IconPencil size={14} stroke={1.5} />
            </button>
          ))}
      </div>
      <LockedFieldWrapper
        locked={lock.isLocked}
        ariaLabel={lock.lockedAriaLabel}
        onClick={lock.onLockedClick}
      >
        <TableCellContent
          field={field}
          padding="px-2"
          isSelected={true}
          value={editing ? editingValue : storedValue}
          onChangeValueAction={(value) => {
            if (editing) setEditingValue(value as string);
          }}
          isEditMode={true}
          copy={!editing}
          viewOnly={!ctx.canWriteGuests}
        />
      </LockedFieldWrapper>
      {editing && error && <p className="body-3-regular font-error">{error}</p>}
      {saving && <CoveringLoadingSpinner />}
    </div>
  );
}

/**
 * One DISPLAY-ONLY built-in: label + the value as text. No input, no form
 * state, nothing saveable.
 *
 * The read rendering is the SAME TableCellContent every list column uses: each
 * TCC* child gates its editor on `(editable || isFilter)` and `editable` comes
 * straight off `field.editable`, which is `false` for every name in
 * READONLY_BUILTIN_ORDER. So `origin` resolves through
 * optionsMultiSingleSelect, `importId` through the FieldsProvider `imports`
 * optionLabels (blank while the catalog is in flight, "Deleted …1a2b" for a
 * removed batch), `checkedIn` renders its tri-state chip and the dates format
 * per locale + event timezone — all without a line of new formatting code.
 *
 * NOT via DataTypeInput: its `value` prop is `string`, and `checkedIn` is a
 * boolean. NOT via ctx.creationFields: none of these keys is in
 * CreationFieldsRef (deliberately — the popup's dirty check is a whole-object
 * JSON.stringify diff), so reading from there yields a silently empty cell.
 * NOT via ctx.getLock: CONDITIONABLE_BUILTIN_FIELDS excludes all of them, and
 * a lock icon on text nobody can edit is noise.
 */
/** A display-only cell has nowhere to write, but TableCellContent requires the
 *  callback. Module-level so it is one stable identity rather than a fresh
 *  closure per render — not because a changing prop would remount anything (it
 *  would not), but so a memo boundary added later cannot be defeated by it. */
const NOOP_CHANGE = () => {};

/**
 * The disabled pill every value-nobody-can-type-into wears: the six display-only
 * built-ins (ReadOnlyBuiltInCell) AND a computed additional field (formula —
 * see the additional-field branch of GuestLayoutFieldInput). One component so
 * the two kinds cannot drift apart in chrome, scale or colour.
 *
 * `data-readonly-field` sits on the OUTER div and `inert` on the INNER one —
 * the tests select `[data-readonly-field] > div` for the pill and read the
 * outer's textContent for label+value; keep that shape.
 */
function ReadOnlyValuePill({
  field,
  label,
  value,
  showValue,
  emptyText = "—",
  title,
}: {
  field: Field;
  label: string;
  value: unknown;
  showValue: boolean;
  emptyText?: string;
  /** Hover explanation for the whole cell (a computed field's formula). */
  title?: string;
}) {
  return (
    <div
      // The value scale, not decoration: TCCSingleSelect and TCCLong put NO
      // font-size class on their read branch and TCCDateTime hardcodes
      // body-2-regular, so without this a value rendered at the 16px browser
      // default under its own 12px label. The guest LIST is uniform only
      // because TableCell puts the same class on the <td> — same mechanism,
      // popup-local. `fontSize` is not a lever; it reaches editors only.
      className="flex flex-col space-y-1.5"
      data-readonly-field={field.name}
      title={title}
    >
      <p className="body-3-medium truncate">{label}</p>
      {/* inert + aria-hidden: the pill LOOKS like a disabled input and IS one —
          not focusable, not tabbable, not announced as a control. Same chrome
          the three sibling popups use, so all four read alike. */}
      <div inert aria-hidden className={`${DETAIL_READ_ONLY_BOX} ${DETAIL_READ_ONLY_TEXT}`}>
      {showValue ? (
        <TableCellContent
          field={field}
          value={value}
          padding=""
          isSelected={true}
          isEditMode={true}
          // The three read branches disagree on scale AND colour; one class for
          // all of them, so a date does not render greyer than a select.
          readTextClass={DETAIL_READ_ONLY_TEXT}
          onChangeValueAction={NOOP_CHANGE}
        />
      ) : (
        // No guest at all (builder canvas preview, pre-create) OR a guest with
        // no value for this field. An em-dash rather than blank: the read
        // branches of TCCLong / TCCDateTime / TCCSingleSelect are a bare <p>, so
        // an empty one collapses the card to label height and leaves nothing to
        // grab or resize — and with no guest at all CHECK_IN_STATUS would
        // otherwise render a confident "not checked in" chip about nobody.
        <span className="font-quaternary">{emptyText}</span>
      )}
      </div>
    </div>
  );
}

function ReadOnlyBuiltInCell({
  field,
  label,
  ctx,
}: {
  field: Field;
  label: string;
  ctx: LayoutFieldCtx;
}) {
  const tOptions = useTranslations("optionsMultiSingleSelect");
  const guest = ctx.guest;
  const raw = guest
    ? (guest[field.name as keyof Guest] as unknown)
    : undefined;
  // CHECK_IN_STATUS is the one dataType whose read branch says something real
  // about an unset value: its third chip IS the answer for a guest nobody
  // scanned. Every other read branch renders an EMPTY element for null — a
  // zero-height line under a label, which reads as a broken field. That is the
  // common case, not an edge (importId is null on every guest that was not
  // CSV-imported), so it gets the same dash the sibling DetailReadOnlyCell uses.
  const showValue =
    !!guest &&
    (field.dataType === DataType.CHECK_IN_STATUS ||
      (raw !== null && raw !== undefined && raw !== ""));
  // Provenance is a question that always has an answer, so it must never render
  // as a blank. A guest with no `origin` predates provenance stamping — that is
  // "unknown", NOT "created here": claiming the latter would invent a fact. The
  // sibling popups have the same rule with the opposite default, because their
  // "no source id" genuinely means created in the admin UI.
  const unknownOrigin =
    !!guest && field.name === "origin" && !showValue
      ? tOptions("ORIGIN_UNKNOWN")
      : null;
  return (
    <ReadOnlyValuePill
      field={field}
      label={label}
      value={raw}
      showValue={showValue}
      emptyText={unknownOrigin ?? "—"}
    />
  );
}

/**
 * One layout cell = one guest field, rendered with the exact same widget the
 * pre-layout popup used for it: status chip for `state`, credential edit flow
 * for `username`/`code`, DataTypeInput for everything else (`address` too —
 * the Photon suggestions are gone, AVV plan P1) — additional fields keep their lock / dependent-option /
 * capacity / inline-error behavior. Display-only built-ins render as text.
 */
export function GuestLayoutFieldInput({
  field,
  ctx,
}: {
  field: Field;
  ctx: LayoutFieldCtx;
}) {
  const t2 = useTranslations("guest-fields");
  const tPopup = useTranslations("popup-create-guest");
  const tTypes = useTranslations("datatypes");

  // --- Additional (custom) field ---------------------------------------
  if (field.additionalField) {
    // A COMPUTED field is read-only, and not because of a permission: the
    // value is derived and re-derived after every write, so anything typed
    // here would be overwritten within the same request. It wears the SAME
    // disabled pill as the display-only built-ins below — not DataTypeInput's
    // `viewOnly`, which rendered TCCLong's bare bold <p> with no chrome at all,
    // i.e. nothing that said "you cannot change this". The formula is the
    // hover explanation. No lock, no error, no default: none of those apply to
    // a value nobody can write.
    //
    // `isComputedField`, never `!!field.formula`: `{}` is the un-computed
    // wrapper the edit dialog sends and the optimistic patch spreads on.
    if (isComputedField(field)) {
      const value = additionalFieldValue(ctx.creationFields, field, ctx.isCreate);
      return (
        <ReadOnlyValuePill
          // TableCellContent's read arm gates on `field.editable`. The
          // catalogue mints a formula field `editable:false`; the guard covers
          // the optimistic patch that spread a formula onto a still-editable
          // field before the refetch.
          field={field.editable ? { ...field, editable: false } : field}
          label={field.translation || field.name}
          value={value}
          showValue={value !== null && value !== undefined && value !== ""}
          title={field.formula?.text || undefined}
        />
      );
    }
    // Human label for the lock-confirm dialog / aria — the display name
    // (translation), never the immutable slug identifier.
    const lock = ctx.getLock(field, field.translation || field.name);
    return (
      <div
        title={lock.isLocked ? tPopup("conditionNotMetHint") : undefined}
        data-condition-unmet={lock.conditionUnmet ? "true" : undefined}
      >
        <DataTypeInput
          info={tTypes(field.dataType)}
          viewOnly={ctx.viewOnly}
          label={field.translation || field.name}
          field={ctx.optionFiltered(field)}
          value={additionalFieldValue(ctx.creationFields, field, ctx.isCreate)}
          onChangeValueAction={(value) => {
            ctx.onClearFieldError?.(field.name);
            ctx.setCreationFields({
              ...ctx.creationFields,
              additionalFields: {
                ...(ctx.creationFields.additionalFields ?? {}),
                [field.name]: value,
              },
            });
          }}
          locked={lock.isLocked}
          lockedAriaLabel={lock.lockedAriaLabel}
          onLockedClick={lock.onLockedClick}
          required={field.required}
          error={ctx.fieldErrors?.[field.name]}
          disabledOptions={ctx.fullOptionsFor(field)}
          guestId={ctx.currentGuestId}
          onValuePersisted={
            ctx.onAdditionalFieldPersisted
              ? (value) => ctx.onAdditionalFieldPersisted!(field.name, value)
              : undefined
          }
          onInvalidChange={
            ctx.onFieldInvalid
              ? (invalid) => ctx.onFieldInvalid!(field.name, invalid)
              : undefined
          }
        />
      </div>
    );
  }

  const label = t2(field.name);

  // --- Portal-link cell (layout-only, W3-3) --------------------------------
  // Its own branch, ABOVE the display-only one: it is opt-in like those, but a
  // live button, not inert text — and it reads nothing from creationFields.
  if (isPortalLinkLayoutField(field)) {
    return <PortalLinkLayoutCell label={label} ctx={ctx} />;
  }

  // --- "Ankunft melden an" cell (layout-only, plan arrival-automation Rev. 4
  // §1.8) — chips + a dialog that writes at once; nothing from creationFields.
  if (isArrivalNoticeLayoutField(field)) {
    return <ArrivalNoticeLayoutCell label={label} ctx={ctx} />;
  }

  // --- Display-only built-in --------------------------------------------
  // Deliberately ABOVE the three lines below: `value` reads creationFields
  // (permanently undefined for these keys → a silent blank cell), `getLock` is
  // meaningless for them, and `setBuiltIn` would write a key the dirty diff
  // compares. Deliberately BELOW the additionalField branch: a CUSTOM field
  // literally named `createdAt` must keep its own editable input.
  if (isReadOnlyLayoutField(field)) {
    return <ReadOnlyBuiltInCell field={field} label={label} ctx={ctx} />;
  }

  const lock = ctx.getLock(field, label);
  const value =
    (ctx.creationFields?.[field.name as keyof CreationFieldsRef] as string) ||
    "";
  const setBuiltIn = (fieldName: string, v: string) =>
    ctx.setCreationFields({ ...ctx.creationFields, [fieldName]: v });

  // --- Status chip -------------------------------------------------------
  if (field.name === "state") {
    return (
      <div className="flex flex-col space-y-1.5">
        <div className="flex items-center gap-1.5">
          <p className="body-3-medium">{label}</p>
          {field.required && (
            <span className="font-error body-3-medium" aria-hidden>
              *
            </span>
          )}
          {lock.isLocked && <FieldLockIcon />}
        </div>
        <LockedFieldWrapper
          locked={lock.isLocked}
          ariaLabel={lock.lockedAriaLabel}
          onClick={lock.onLockedClick}
        >
          <TableCellContent
            fontSize="body-3-bold"
            border="border outline-quaternary"
            height="h-8"
            field={field}
            value={value}
            viewOnly={!ctx.canWriteGuests}
            isSelected={true}
            isEditMode={true}
            onChangeValueAction={(v) => setBuiltIn(field.name, v as string)}
          />
        </LockedFieldWrapper>
      </div>
    );
  }

  // --- Credentials (username / code) — special inline save flow ----------
  if (field.name === "username" || field.name === "code") {
    return <CredentialCell field={field} ctx={ctx} />;
  }

  // --- Every other built-in ----------------------------------------------
  // A built-in the backend refuses on the update path is shown but not
  // editable, with the reason on hover. NOT via `locked` — that prop means
  // "filtered out by a condition, click to override" everywhere else, and this
  // can't be overridden. `disabled` reaches TCCSingleSelect's read arm, the
  // same treatment the create dialog already gives this field when it is
  // opened pre-scoped.
  //
  // Two reasons, two sentences: the field-wide flag, and (today's live case)
  // `guestType` on a guest whose request is still PENDING. The state comes off
  // `ctx.guest`, which is the FETCHED row — the popup opened from the Anfragen
  // page re-fetches with GUEST_REFETCH_FIELDS, so `acceptanceState` is really
  // there rather than undefined.
  const blockReason = guestTypeBlockReason(
    field.name,
    field.additionalField,
    ctx.guest?.acceptanceState,
  );
  const blockedOnUpdate = blockReason !== null;
  // Third reason, create-side: the lane fixed the type (see ctx.guestTypeLocked).
  const laneLocksType =
    !field.additionalField && field.name === "guestType" && !!ctx.guestTypeLocked;
  const blockedHint =
    blockReason === "pending"
      ? t2("guestTypeLockedWhilePendingHint")
      : blockReason === "unsupported"
        ? t2("guestTypeNotChangeableHint")
        : undefined;

  return (
    <div title={blockedHint}>
      <DataTypeInput
        field={ctx.optionFiltered(field)}
        viewOnly={!ctx.canWriteGuests}
        disabled={blockedOnUpdate || laneLocksType}
        info={blockedHint}
        value={value}
        label={label}
        onChangeValueAction={(v) => setBuiltIn(field.name, v as string)}
        locked={lock.isLocked}
        lockedAriaLabel={lock.lockedAriaLabel}
        onLockedClick={lock.onLockedClick}
        required={field.required}
        onInvalidChange={
          ctx.onFieldInvalid
            ? (invalid) => ctx.onFieldInvalid!(field.name, invalid)
            : undefined
        }
      />
    </div>
  );
}
